import React from 'react';

import {Ownable} from 'auth/PermissionConstants';
import {getOwnerChoices, toServerCollaborators} from 'create-components/OwnableUtil';
import {RootCrumb} from 'nav/Breadcrumbs';
import ListingHeader from 'nav/sidepanel/header/ListingHeader';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ExpandedTabContainer, Tab} from 'ui-library/ExpandedTabContainer';
import ModalAnchor from 'ui-library/ModalAnchor';
import * as nyc from 'ui-library/NYC';
import {FetchWorkflowTemplateResponse} from 'workflows/dao/WorkflowTemplateDAO';

import NewWorkflowButton from './components/NewWorkflowButton';
import WorkflowTemplateBaseSettingsModal from './modals/WorkflowTemplateBaseSettingsModal';
import {getWorkflowDisplayString} from './util/WorkflowUtil';
import * as WorkflowActions from './WorkflowActions';
import {WORKFLOW_SCREEN, WorkflowScreen, WorkflowTemplate} from './WorkflowConstants';
import WorkflowDashboardScreen from './WorkflowDashboardScreen';
import WorkflowDashboardTemplatesScreen from './WorkflowDashboardTemplatesScreen';
import * as WorkflowTemplateActions from './WorkflowTemplateActions';

export interface WorkflowDashboardContainerProps {
  workflowTemplates?: Array<WorkflowTemplate> | null;
  screen: WorkflowScreen.WORKFLOW_DASHBOARD | WorkflowScreen.WORKFLOW_DASHBOARD_TEMPLATES;
}

interface State {
  isCreateWorkflowTemplateModalOpen: boolean;
}

class WorkflowDashboardContainer extends React.Component<WorkflowDashboardContainerProps, State> {
  override state: State = {
    isCreateWorkflowTemplateModalOpen: false,
  };

  _handleCreateNewWorkflowTemplate = ({
    name,
    owner,
    ownerInfo,
    prefix,
    collaborators,
  }: Ownable): JQueryPromise<FetchWorkflowTemplateResponse> =>
    WorkflowTemplateActions.createWorkflowTemplate({
      name,
      owner,
      ownerInfo,
      prefix,
      collaborators: toServerCollaborators(collaborators),
    });

  _handleOpenCreateWorkflowTemplateModal = (): void => {
    this.setState({isCreateWorkflowTemplateModalOpen: true});
  };

  _handleCloseCreateWorkflowTemplateModal = (): void => {
    this.setState({isCreateWorkflowTemplateModalOpen: false});
  };

  _handleSetActiveTab = (key: WorkflowScreen): void => {
    if (key === WORKFLOW_SCREEN.WORKFLOW_DASHBOARD) {
      WorkflowActions.openWorkflowDashboardScreen();
    } else {
      WorkflowActions.openWorkflowDashboardTemplatesScreen();
    }
  };

  _renderHeaderButton(): JSX.Element {
    if (this.props.screen === WORKFLOW_SCREEN.WORKFLOW_DASHBOARD) {
      return <NewWorkflowButton />;
    } else {
      const disabled = getOwnerChoices().length === 0;

      return (
        <Tooltip
          title={
            disabled ? 'Please contact your administrator to create a workflow template' : 'New Template'
          }
          placement={TooltipPlacement.BOTTOM}
          container="body"
        >
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            icon="compose"
            onClick={this._handleOpenCreateWorkflowTemplateModal}
            size={ButtonSize.SMALL}
            text="New Template"
            disabled={disabled}
          />
        </Tooltip>
      );
    }
  }

  override render(): JSX.Element {
    return (
      <div>
        {this.state.isCreateWorkflowTemplateModalOpen && (
          <ModalAnchor open={true}>
            <WorkflowTemplateBaseSettingsModal
              onRequestClose={this._handleCloseCreateWorkflowTemplateModal}
              doSubmit={this._handleCreateNewWorkflowTemplate}
            />
          </ModalAnchor>
        )}
        <nyc.Container
          className="expandedContainer expandedContainer--fullScreen"
          size="lg"
          dataTestAttrs={{component: 'WorkflowDashboardContainer', element: 'root', key: 'expanded'}}
        >
          <nyc.Row>
            <nyc.Col lg={1} />
            <nyc.Col lg={20}>
              <ListingHeader
                isExpanded={true}
                buttons={this._renderHeaderButton()}
                crumbs={<RootCrumb text={getWorkflowDisplayString('Legacy Workflows', 'Workflows')} />}
                showShadow={false}
              />
            </nyc.Col>
            <nyc.Col lg={1} />
          </nyc.Row>
          <nyc.Row fluid={true}>
            <nyc.Col lg={1} />
            <nyc.Col lg={20} className="u-flex-display u-vertical-flex">
              <ExpandedTabContainer
                activeTab={this.props.screen}
                className="u-flex-full-height"
                onRequestTabChange={this._handleSetActiveTab}
              >
                <Tab
                  tabKey={WORKFLOW_SCREEN.WORKFLOW_DASHBOARD}
                  title={getWorkflowDisplayString('Legacy Workflows', 'Workflows')}
                >
                  <WorkflowDashboardScreen />
                </Tab>
                <Tab tabKey={WORKFLOW_SCREEN.WORKFLOW_DASHBOARD_TEMPLATES} title="Templates">
                  <WorkflowDashboardTemplatesScreen workflowTemplates={this.props.workflowTemplates} />
                </Tab>
              </ExpandedTabContainer>
            </nyc.Col>
            <nyc.Col lg={1} />
          </nyc.Row>
        </nyc.Container>
      </div>
    );
  }
}

export default WorkflowDashboardContainer;
